import {
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  DestroyRef,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { AppSharedModule } from 'app/app-shared.module';
import { WidgetDataSource } from 'app/models/widget-data-source.model';
import { notAllCheckedValidator } from 'app/utils/customValidators';
import { TaxonomyPickerComponent } from '../../taxonomy-picker/taxonomy-picker.component';

export interface GridBreakdownTaxonomyConfig {
  analyticItem: 'taxonomy';
  leafNodesOnly: boolean | undefined;
  showTopTaxonomyNum: number;
  customItemsSelected: boolean;
  customTaxonomyItems?: string[];
}
@Component({
  selector: 'app-grid-breakdown-taxonomy-config',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [AppSharedModule, TaxonomyPickerComponent],
  templateUrl: './grid-breakdown-taxonomy-config.component.html',
  styleUrl: './grid-breakdown-taxonomy-config.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GridBreakdownTaxonomyConfigComponent implements OnInit {
  projectId = input<string>();
  widgetDataSource = input<WidgetDataSource>();
  taxonomyPayload = input<GridBreakdownTaxonomyConfig>();

  isValid = output<boolean>();
  onValueChange = output<GridBreakdownTaxonomyConfig>();

  private fb = inject(FormBuilder);
  private destroyRef = inject(DestroyRef);

  private customTaxonomyItems = signal<string[] | null>(null);

  form = this.fb.group({
    selectionType: ['topItems'],
    numItems: [20, [Validators.required, Validators.min(1), Validators.max(20)]],
    nodeOptions: this.fb.group(
      {
        excludeLeafNodes: [false],
        excludeNonLeafNodes: [false],
      },
      { validators: notAllCheckedValidator() }
    ),
  });

  ngOnInit(): void {
    const payload = this.taxonomyPayload();
    if (payload) {
      this.customTaxonomyItems.set(payload.customTaxonomyItems);
      this.loadForm();
    }
    this.emitCurrentValue();

    this.form.controls.selectionType.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        if (value === 'customItems') {
          this.customTaxonomyItems.set(this.taxonomyPayload().customTaxonomyItems);
          this.numItems.clearValidators();
          this.numItems.setValue(null, { emitEvent: false });
          this.nodeOptions.get('excludeLeafNodes').setValue(false, { emitEvent: false });
          this.nodeOptions.get('excludeNonLeafNodes').setValue(false, { emitEvent: false });
          this.nodeOptions.clearValidators();
        } else {
          this.customTaxonomyItems.set(null);
          this.numItems.setValue(20, { emitEvent: false });
          this.numItems.setValidators([Validators.required, Validators.min(1), Validators.max(20)]);
          this.nodeOptions.setValidators(notAllCheckedValidator());
        }
        this.numItems.updateValueAndValidity({ emitEvent: false });
        this.nodeOptions.updateValueAndValidity({ emitEvent: false });
        this.emitCurrentValue();
      });

    this.form.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.emitCurrentValue());

    this.form.statusChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.isValid.emit(this.isCurrentStateValid());
    });
  }

  get selectionType() {
    return this.form.get('selectionType');
  }

  get numItems() {
    return this.form.get('numItems');
  }

  get nodeOptions() {
    return this.form.get('nodeOptions') as FormGroup;
  }

  private loadForm() {
    this.form.patchValue(
      {
        selectionType: this.taxonomyPayload().customItemsSelected ? 'customItems' : 'topItems',
        numItems: this.taxonomyPayload().showTopTaxonomyNum || 20,
        nodeOptions: {
          excludeLeafNodes: this.taxonomyPayload().leafNodesOnly === false,
          excludeNonLeafNodes: this.taxonomyPayload().leafNodesOnly === true,
        },
      },
      { emitEvent: false }
    );
  }

  onTaxonomySelectionChanged($event: { id: string; name: string }[]): void {
    this.customTaxonomyItems.set($event.map((item) => item.id));
    this.emitCurrentValue();
  }

  private emitCurrentValue(): void {
    this.isValid.emit(this.isCurrentStateValid());
    this.onValueChange.emit({
      analyticItem: 'taxonomy',
      leafNodesOnly: this.resolveLeafNodesOnly(),
      showTopTaxonomyNum: this.numItems.value,
      customItemsSelected: this.selectionType.value === 'customItems',
      customTaxonomyItems: this.customTaxonomyItems(),
    });
  }

  private resolveLeafNodesOnly(): boolean | undefined {
    const { excludeLeafNodes, excludeNonLeafNodes } = this.nodeOptions.value;
    if (!excludeLeafNodes && excludeNonLeafNodes) {
      return true;
    }
    if (excludeLeafNodes && !excludeNonLeafNodes) {
      return false;
    }
    return undefined;
  }

  private isCurrentStateValid(): boolean {
    if (!this.form.valid) {
      return false;
    }
    if (this.selectionType.value !== 'customItems') {
      return true;
    }
    return this.customTaxonomyItems()?.length > 0;
  }
}
